/* Own the existing tab and image-dialog behavior without rebuilding tab content. */
(() => {
  if (window.TCLTabsContent) {
    window.TCLTabsContent.init(document);
    return;
  }

  const instances = new Map();
  const roots = (scope) => [
    ...(scope.matches?.('[data-tabs-content]') ? [scope] : []),
    ...scope.querySelectorAll('[data-tabs-content]'),
  ];

  class TabsContent {
    constructor(root) {
      this.root = root;
      this.buttons = Array.from(root.querySelectorAll('.tab-button'));
      this.panels = Array.from(root.querySelectorAll('.tab-panel'));
      this.frame = null;
      this.modal = null;
      this.opener = null;
      this.onClick = this.onClick.bind(this);
      this.onEscape = this.onEscape.bind(this);
      root.addEventListener('click', this.onClick);
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-tabs-content]') !== this.root) return;
      const tab = target.closest('.tab-button');
      if (tab && this.buttons.includes(tab)) {
        this.showTab(tab);
        return;
      }
      const opener = target.closest('.image-button--modal');
      if (opener) {
        event.preventDefault();
        this.openModal(opener);
      }
    }

    showTab(button) {
      const panel = this.panels.find((item) => item.id === button.getAttribute('data-tab'));
      if (!panel) return;
      this.buttons.forEach((item) => item.classList.toggle('active', item === button));
      this.panels.forEach((item) => {
        item.classList.toggle('active', item === panel);
        item.setAttribute('aria-hidden', item === panel ? 'false' : 'true');
      });
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!panel.isConnected || !panel.classList.contains('active')) return;
        // Reuse Dawn's public sizing method, only after this panel becomes visible.
        panel.querySelectorAll('slider-component').forEach((slider) => slider.resetPages?.());
      });
    }

    selectBlock(event) {
      const target = event.target instanceof Element ? event.target : null;
      const panel = target?.closest('.tab-panel');
      const button = this.buttons.find((item) => item === target?.closest('.tab-button')
        || item.getAttribute('data-tab') === panel?.id
        || item.getAttribute('data-tab') === `tab-${event.detail?.blockId}`);
      if (button) this.showTab(button);
    }

    openModal(opener) {
      const modalId = opener.getAttribute('data-modal-id');
      if (!modalId) return;
      const content = document.getElementById(`${modalId}-content`);
      if (!content || !this.root.contains(content)) return;
      this.closeModal(false);
      const modal = document.createElement('div');
      modal.className = 'image-modal';
      modal.id = modalId;
      modal.innerHTML = `
        <div class="image-modal-overlay">
          <div class="image-modal-overlay_box">
            <div class="image-modal-content">
              <button type="button" class="image-modal-close" aria-label="Close modal">&times;</button>
              <h2 class="image-modal-title"></h2>
              <div class="image-modal-body"></div>
            </div>
          </div>
        </div>`;
      modal.querySelector('.image-modal-title').textContent = content.querySelector('h2')?.textContent || 'Modal Title';
      modal.querySelector('.image-modal-body').innerHTML = content.querySelector('.modal-content-body')?.innerHTML || '<p>No content available.</p>';
      modal.addEventListener('click', (event) => {
        if (event.target.closest?.('.image-modal-close') || event.target === modal.querySelector('.image-modal-overlay')) this.closeModal();
      });
      this.modal = modal;
      this.opener = opener;
      document.body.appendChild(modal);
      modal.style.display = 'flex';
      document.addEventListener('keydown', this.onEscape);
    }

    onEscape(event) {
      if (event.key === 'Escape') this.closeModal();
    }

    closeModal(returnFocus = true) {
      if (!this.modal) return;
      this.modal.remove();
      this.modal = null;
      document.removeEventListener('keydown', this.onEscape);
      if (returnFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    destroy() {
      this.root.removeEventListener('click', this.onClick);
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.closeModal(false);
    }
  }

  const init = (scope) => roots(scope).forEach((root) => {
    if (!instances.has(root)) instances.set(root, new TabsContent(root));
  });
  const dispose = (scope) => {
    for (const [root, instance] of instances) {
      if (root === scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  };
  window.TCLTabsContent = { init };
  document.addEventListener('shopify:section:load', (event) => {
    dispose(event.target);
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => dispose(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-tabs-content]');
    if (root) instances.get(root)?.selectBlock(event);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();